import { createMemo, createSignal, For, Index, Match, Show, Switch } from 'solid-js';
import Text from './Text';
import LocationPicker from './LocationPicker';
import CoordinateInput, { type Coordinate } from './CoordinateInput';
import { AMPERSAND_CODE_REGEX, formatAmpersandText } from './MinecraftText';
import { showConfirm } from './Dialog';
import { useToast } from './MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { useApi, isOk } from '../api';
import StorageSelect from './StorageSelect';
import type { StorageInfo } from '../services/inventoryApi';
import { firstError } from '../api/envelope';

export type SettingType = 'string' | 'number' | 'boolean' | 'enum' | 'object' | 'list' | 'location' | 'time';

export interface SettingDef {
    name: string;
    label: string;
    type: SettingType;
    default?: unknown;
    description?: string;
    options?: string[];
    fields?: SettingDef[];
    elementSchema?: SettingDef;
    optionSource?: string;
}

export type DynamicOption = string | { value: string; label: string };

function normalizeOption(opt: DynamicOption): { value: string; label: string } {
    return typeof opt === 'string' ? { value: opt, label: opt } : opt;
}

interface PluginAutoSettingsFormProps {
    botId: string;
    pluginName: string;
    definitions: SettingDef[];
    values: Record<string, unknown>;
    onChange: (next: Record<string, unknown>) => void;
    onSave: () => void;
    onReset: () => void | Promise<void>;
    saving?: boolean;
    saveTargetCount?: number;
    dynamicOptions?: Record<string, DynamicOption[]>;
    storages?: StorageInfo[];
}

export default function PluginAutoSettingsForm(props: PluginAutoSettingsFormProps) {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const [resetting, setResetting] = createSignal(false);
    const saveLabel = () => (props.saveTargetCount ?? 1) > 1
        ? t('BOT_PLUGINS_SAVE_FOR_BOTS', String(props.saveTargetCount))
        : t('BOT_PLUGINS_SAVE');

    const resetToDefaults = async () => {
        if (!await showConfirm(t('BOT_PLUGINS_RESET_CONFIRM', props.pluginName), true, { confirmLabel: t('BOT_PLUGINS_RESET') })) return;
        setResetting(true);
        const res = await api.botPlugin.resetPluginSettings(props.botId, props.pluginName);
        setResetting(false);
        if (isOk(res.data)) {
            toast?.info(t('BOT_PLUGINS_RESET_DONE'));
            await props.onReset();
        } else {
            toast?.error(firstError(res.data) ?? t('BOT_PLUGINS_RESET_FAILED'));
        }
    };

    return (
        <form
            class="v"
            style={{ gap: 'var(--spacing-md)' }}
            onSubmit={(e) => { e.preventDefault(); props.onSave(); }}
        >
            <ObjectFields
                fields={props.definitions}
                values={props.values}
                onChange={props.onChange}
                dynamicOptions={props.dynamicOptions}
                storages={props.storages}
            />
            <div class="h" style={{ "justify-content": 'flex-end', gap: 'var(--spacing-sm)', "flex-wrap": 'wrap' }}>
                <button
                    type="button"
                    class="btn btn-secondary"
                    style={{ "margin-right": 'auto' }}
                    disabled={resetting() || !!props.saving}
                    onClick={() => void resetToDefaults()}
                    title={t('BOT_PLUGINS_RESET_TITLE')}
                >
                    <Show when={resetting()} fallback={<><i class="fa-solid fa-rotate-left" /> <Text val="BOT_PLUGINS_RESET" /></>}>
                        <i class="fa-solid fa-spinner fa-spin" /> <Text val="BOT_PLUGINS_RESET" />
                    </Show>
                </button>
                <button type="submit" class="btn btn-primary" disabled={!!props.saving || resetting()}>
                    <Show when={props.saving} fallback={<i class="fa-solid fa-floppy-disk" />}>
                        <i class="fa-solid fa-spinner fa-spin" />
                    </Show>
                    {' '}{saveLabel()}
                </button>
            </div>
        </form>
    );
}

interface ObjectFieldsProps {
    fields: SettingDef[];
    values: Record<string, unknown>;
    onChange: (next: Record<string, unknown>) => void;
    dynamicOptions?: Record<string, DynamicOption[]>;
    storages?: StorageInfo[];
}

interface CoordinateRow {
    label: string;
    description?: string;
    names: { x: string; y: string; z: string };
}

type FieldRow = { def: SettingDef } | { coordinate: CoordinateRow };

// Plugins declare a block position as three number settings (LampX, LampY, LampZ); render those as one coordinate.
export function groupCoordinateFields(fields: SettingDef[]): FieldRow[] {
    const rows: FieldRow[] = [];
    for (let i = 0; i < fields.length; i++) {
        const [xDef, yDef, zDef] = [fields[i], fields[i + 1], fields[i + 2]];
        const prefix = xDef.name.slice(0, -1);
        const isTriple = xDef.name.endsWith('X') && yDef?.name === prefix + 'Y' && zDef?.name === prefix + 'Z'
            && [xDef, yDef, zDef].every(def => def.type === 'number' && !def.optionSource);
        if (!isTriple) {
            rows.push({ def: xDef });
            continue;
        }
        rows.push({ coordinate: {
            label: xDef.label.replace(/\s*X$/, ''),
            description: xDef.description,
            names: { x: xDef.name, y: yDef.name, z: zDef.name },
        } });
        i += 2;
    }
    return rows;
}

function ObjectFields(props: ObjectFieldsProps) {
    const setField = (name: string, value: unknown) => {
        props.onChange({ ...props.values, [name]: value });
    };
    const fieldValue = (name: string) => props.values[name] ?? props.fields.find(def => def.name === name)?.default;
    // Parents re-read props.fields on every refetch; fresh row objects would remount every control and close open pickers.
    const fields = createMemo(() => props.fields);
    const rows = createMemo(() => groupCoordinateFields(fields()));
    return (
        <div class="v" style={{ gap: 'var(--spacing-md)' }}>
            <For each={rows()}>{(row) => 'def' in row
                ? <SingleField
                    def={row.def}
                    value={fieldValue(row.def.name)}
                    onChange={(v) => setField(row.def.name, v)}
                    dynamicOptions={props.dynamicOptions}
                    storages={props.storages}
                />
                : <CoordinateField
                    row={row.coordinate}
                    value={{
                        x: Number(fieldValue(row.coordinate.names.x) ?? 0),
                        y: Number(fieldValue(row.coordinate.names.y) ?? 0),
                        z: Number(fieldValue(row.coordinate.names.z) ?? 0),
                    }}
                    onChange={(next) => props.onChange({
                        ...props.values,
                        [row.coordinate.names.x]: next.x,
                        [row.coordinate.names.y]: next.y,
                        [row.coordinate.names.z]: next.z,
                    })}
                />
            }</For>
        </div>
    );
}

function CoordinateField(props: { row: CoordinateRow; value: Coordinate; onChange: (next: Coordinate) => void }) {
    return (
        <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
            <span style={{ "font-size": 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                {props.row.label}
            </span>
            <Show when={props.row.description}>
                <span style={{ "font-size": 'var(--font-size-xs)', color: 'var(--dark-text-muted)' }}>
                    {props.row.description}
                </span>
            </Show>
            <CoordinateInput value={props.value} onChange={props.onChange} />
        </div>
    );
}

function SingleField(props: FieldControlProps) {
    return (
        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
            <span style={{ "font-size": 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                {props.def.label}
            </span>
            <Show when={props.def.description}>
                <span style={{ "font-size": 'var(--font-size-xs)', color: 'var(--dark-text-muted)' }}>
                    {props.def.description}
                </span>
            </Show>
            <FieldControl {...props} />
        </label>
    );
}

interface FieldControlProps {
    def: SettingDef;
    value: unknown;
    onChange: (next: unknown) => void;
    dynamicOptions?: Record<string, DynamicOption[]>;
    storages?: StorageInfo[];
}

function FieldControl(props: FieldControlProps) {
    const { t } = useTranslation();
    const dynamicOptions = () => {
        if (!props.def.optionSource) return null;
        return props.dynamicOptions?.[props.def.optionSource]?.map(normalizeOption) ?? null;
    };
    const isMultiSelect = () => props.def.type === 'list' && dynamicOptions() !== null;
    const isSingleSelect = () =>
        props.def.type !== 'list' && props.def.type !== 'object' && dynamicOptions() !== null;

    return (
        <Switch fallback={<ScalarControl {...props} />}>
            <Match when={props.def.type === 'object'}>
                <ObjectControl {...props} />
            </Match>
            <Match when={props.def.type === 'location'}>
                <LocationPicker
                    value={props.value != null ? String(props.value) : ''}
                    onChange={(next) => props.onChange(next || null)}
                    optional
                />
            </Match>
            <Match when={isMultiSelect()}>
                <DynamicCheckboxList
                    options={dynamicOptions()!}
                    value={props.value}
                    onChange={props.onChange}
                />
            </Match>
            <Match when={isSingleSelect() && props.def.optionSource === 'Storages' && props.storages}>
                {(storages) => (
                    <StorageSelect
                        storages={storages()}
                        value={selectedOptionValue(props.value)}
                        onChange={(storageId) => props.onChange(storageId || null)}
                        emptyOption={{ label: t('BOT_PLUGINS_SELECT_NONE'), icon: 'fa-ban' }}
                    />
                )}
            </Match>
            <Match when={isSingleSelect()}>
                <DynamicSelect
                    options={dynamicOptions()!}
                    value={props.value}
                    onChange={props.onChange}
                />
            </Match>
            <Match when={props.def.type === 'list'}>
                <ListControl {...props} />
            </Match>
        </Switch>
    );
}

interface DynamicOptionsProps {
    options: { value: string; label: string }[];
    value: unknown;
    onChange: (next: unknown) => void;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

function selectedOptionValue(value: unknown) {
    const text = value != null ? String(value) : '';
    return text === EMPTY_GUID ? '' : text;
}

function DynamicSelect(props: DynamicOptionsProps) {
    const current = () => selectedOptionValue(props.value);
    return (
        <select value={current()} onChange={(e) => props.onChange(e.currentTarget.value || null)}>
            <option value=""><Text val="BOT_PLUGINS_SELECT_NONE" /></option>
            <For each={props.options}>{(opt) =>
                <option value={opt.value} selected={opt.value === current()}>{opt.label}</option>
            }</For>
        </select>
    );
}

function DynamicCheckboxList(props: DynamicOptionsProps) {
    const selected = () => Array.isArray(props.value) ? (props.value as string[]) : [];

    // Options come from the live bot; an offline bot offers none, and its saved picks must stay visible and untickable.
    const optionsWithSelected = createMemo(() => {
        const offered = new Set(props.options.map(opt => opt.value));
        const savedOnly = selected().filter(value => !offered.has(value)).map(value => ({ value, label: value }));
        return [...props.options, ...savedOnly];
    });

    const toggle = (option: string, checked: boolean) => {
        const cur = selected();
        if (checked) {
            if (cur.includes(option)) return;
            props.onChange([...cur, option]);
        } else {
            props.onChange(cur.filter(v => v !== option));
        }
    };

    return (
        <div class="v" style={{
            gap: 'var(--spacing-xs)',
            padding: 'var(--spacing-md)',
            background: 'var(--dark-bg-tertiary)',
            "border-radius": 'var(--radius-md)',
            "max-height": '14rem',
            "overflow-y": 'auto',
        }}>
            <Show when={optionsWithSelected().length === 0}>
                <span style={{ color: 'var(--dark-text-muted)', "font-size": 'var(--font-size-sm)' }}>
                    <Text val="BOT_PLUGINS_LIST_EMPTY" />
                </span>
            </Show>
            <For each={optionsWithSelected()}>{(opt) =>
                <label class="h" style={{ "align-items": 'center', gap: 'var(--spacing-sm)', cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        checked={selected().includes(opt.value)}
                        onChange={(e) => toggle(opt.value, e.currentTarget.checked)}
                    />
                    <span style={{ "font-size": 'var(--font-size-sm)' }}>{opt.label}</span>
                </label>
            }</For>
        </div>
    );
}

function ScalarControl(props: FieldControlProps) {
    const { t } = useTranslation();
    const def = props.def;

    if (def.type === 'enum' && def.options) {
        return (
            <select
                value={String(props.value ?? def.default ?? '')}
                onChange={(e) => props.onChange(e.currentTarget.value)}
            >
                <For each={def.options}>{(opt) => <option value={opt}>{opt}</option>}</For>
            </select>
        );
    }
    if (def.type === 'boolean') {
        const checked = props.value === true || (props.value == null && def.default === true);
        return (
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => props.onChange(e.currentTarget.checked)}
            />
        );
    }
    if (def.type === 'time') {
        // The backend hands back TimeOnly as HH:mm:ss, which <input type="time"> will not display.
        const hourAndMinute = () => props.value != null ? String(props.value).slice(0, 5) : '';
        return (
            <input
                type="time"
                value={hourAndMinute()}
                onInput={(e) => props.onChange(e.currentTarget.value || null)}
            />
        );
    }
    if (def.type === 'number') {
        const numericValue = () => props.value != null ? String(props.value) : '';
        const seconds = () => def.name.endsWith('Seconds') ? Number(props.value) : NaN;

        return (
            <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                <input
                    type="number"
                    step="any"
                    placeholder={def.default != null ? String(def.default) : ''}
                    value={numericValue()}
                    onInput={(e) => {
                        const v = e.currentTarget.value;
                        props.onChange(v === '' ? null : Number(v));
                    }}
                />
                <Show when={Number.isFinite(seconds())}>
                    <span style={{ "font-size": 'var(--font-size-xs)', color: 'var(--dark-text-muted)' }}>
                        {t("BOT_PLUGINS_SECONDS_AS_TIME", Math.floor(Math.abs(seconds()) / 60), Math.abs(seconds()) % 60)}
                    </span>
                </Show>
            </div>
        );
    }
    const text = () => props.value != null ? String(props.value) : '';

    return (
        <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
            <input
                type="text"
                placeholder={def.default != null ? String(def.default) : ''}
                value={text()}
                onInput={(e) => props.onChange(e.currentTarget.value)}
            />
            <Show when={AMPERSAND_CODE_REGEX.test(text())}>
                <div style={{
                    padding: 'var(--spacing-xs) var(--spacing-sm)',
                    background: 'var(--dark-bg-elevated)',
                    "border-radius": 'var(--radius-md)',
                    "font-family": 'var(--font-family-minecraft)',
                }}>
                    {formatAmpersandText(text())}
                </div>
            </Show>
        </div>
    );
}

function ObjectControl(props: FieldControlProps) {
    return (
        <div style={{
            padding: 'var(--spacing-md)',
            background: 'var(--dark-bg-tertiary)',
            "border-radius": 'var(--radius-md)',
        }}>
            <ObjectFields
                fields={props.def.fields ?? []}
                values={(props.value as Record<string, unknown> | null | undefined) ?? {}}
                onChange={props.onChange}
                dynamicOptions={props.dynamicOptions}
                storages={props.storages}
            />
        </div>
    );
}

function ListControl(props: FieldControlProps) {
    const { t } = useTranslation();
    const items = () => Array.isArray(props.value) ? (props.value as unknown[]) : [];
    const element = props.def.elementSchema;

    const addItem = () => {
        const next = [...items()];
        next.push(defaultForSchema(element));
        props.onChange(next);
    };
    const removeItem = (i: number) => {
        const next = [...items()];
        next.splice(i, 1);
        props.onChange(next);
    };
    const updateItem = (i: number, value: unknown) => {
        const next = [...items()];
        next[i] = value;
        props.onChange(next);
    };
    const moveItem = (i: number, dir: -1 | 1) => {
        const next = [...items()];
        const j = i + dir;
        if (j < 0 || j >= next.length) return;
        [next[i], next[j]] = [next[j], next[i]];
        props.onChange(next);
    };

    return (
        <div class="v" style={{ gap: 'var(--spacing-sm)' }}>
            <Show when={items().length === 0}>
                <div style={{ color: 'var(--dark-text-muted)', "font-size": 'var(--font-size-sm)' }}>
                    <Text val="BOT_PLUGINS_LIST_EMPTY" />
                </div>
            </Show>
            <Index each={items()}>{(item, i) =>
                <div class="v" style={{
                    padding: 'var(--spacing-md)',
                    background: 'var(--dark-bg-tertiary)',
                    "border-radius": 'var(--radius-md)',
                    gap: 'var(--spacing-sm)',
                }}>
                    <div class="h" style={{ "align-items": 'center', "justify-content": 'space-between' }}>
                        <span style={{ "font-size": 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>#{i + 1}</span>
                        <div class="h" style={{ gap: 'var(--spacing-xs)' }}>
                            <button type="button" class="btn btn-icon btn-secondary"
                                onClick={() => moveItem(i, -1)} disabled={i === 0} title={t("MOVE_UP")}>
                                <i class="fa-solid fa-arrow-up" />
                            </button>
                            <button type="button" class="btn btn-icon btn-secondary"
                                onClick={() => moveItem(i, 1)} disabled={i === items().length - 1} title={t("MOVE_DOWN")}>
                                <i class="fa-solid fa-arrow-down" />
                            </button>
                            <button type="button" class="btn btn-icon btn-danger"
                                onClick={() => removeItem(i)} title={t("REMOVE")}>
                                <i class="fa-solid fa-trash" />
                            </button>
                        </div>
                    </div>
                    <Show when={element}>
                        {(el) => (
                            <FieldControl
                                def={{ ...el(), name: '', label: '' }}
                                value={item()}
                                onChange={(v) => updateItem(i, v)}
                            />
                        )}
                    </Show>
                </div>
            }</Index>
            <button type="button" class="btn btn-secondary btn-sm" onClick={addItem}>
                <i class="fa-solid fa-plus" /> <Text val="BOT_PLUGINS_LIST_ADD" />
            </button>
        </div>
    );
}

export function mergePluginDefaults(values: Record<string, unknown>, defs: SettingDef[]): Record<string, unknown> {
    const result: Record<string, unknown> = { ...values };
    for (const def of defs) {
        const existing = result[def.name];
        result[def.name] = (existing === undefined || existing === null)
            ? defaultForSchema(def)
            : mergeDefaults(existing, def);
    }
    return result;
}

function mergeDefaults(value: unknown, schema: SettingDef): unknown {
    switch (schema.type) {
        case 'object': {
            const base = (value && typeof value === 'object' && !Array.isArray(value))
                ? value as Record<string, unknown>
                : {};
            return mergePluginDefaults(base, schema.fields ?? []);
        }
        case 'list': {
            const arr = Array.isArray(value) ? value : [];
            return schema.elementSchema
                ? arr.map(item => mergeDefaults(item, schema.elementSchema!))
                : arr;
        }
        default:
            return (value === undefined || value === null) ? defaultForSchema(schema) : value;
    }
}

export function defaultForSchema(schema: SettingDef | undefined): unknown {
    if (!schema) return null;
    if (schema.default !== undefined && schema.default !== null) return schema.default;
    switch (schema.type) {
        case 'object': {
            const obj: Record<string, unknown> = {};
            for (const f of schema.fields ?? []) obj[f.name] = defaultForSchema(f);
            return obj;
        }
        case 'list': return [];
        // An empty string is not a TimeOnly, so an unset time has to stay null or saving rejects it.
        case 'time': return null;
        case 'number': return 0;
        case 'boolean': return false;
        case 'enum': return schema.options?.[0] ?? '';
        default: return '';
    }
}
